import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { getRequest } from "src/pages/request/actions";
import { casesApi } from "./api";

// The steps of a case that span components: what happened to an email, the
// "not paid" question, and each traveller's visa.

// The partner platform that sent the case (e.g. Safrati Travel), or null.
// Its traveller has no email with us: the partner hears about every change
// and tells them.
export const partnerName = (caseData) => caseData?.partner?.name || null;

// One number per application (QA nadia-14): the case is #1611 and its
// travellers are 1611-1, 1611-2, in the case's order. A traveller's own
// request id stays inside the system.
export const travellerNumber = (caseData, traveller) => `${caseData?.id}-${traveller?.number}`;
export const travellerName = (traveller) => traveller?.name || `Traveller ${traveller?.number}`;
// "Zz Tester (1611-1)"
export const travellerLabel = (caseData, traveller) => `${travellerName(traveller)} (${travellerNumber(caseData, traveller)})`;

// A partner's case is billed to the partner, so it counts as payment
// arranged. The owner has not confirmed that billing arrangement yet: set
// this to false to ask "isn't paid, continue?" on partner cases too.
const PARTNER_BILLING_COUNTS_AS_PAID = true;
export const paymentArranged = (caseData) => !!caseData?.is_paid || (PARTNER_BILLING_COUNTS_AS_PAID && !!partnerName(caseData));

// Paid / Not paid, or "Billed to <partner>" for a partner's case (billed to
// the partner, never paid online).
export function PaymentChip({ caseData }) {
  const partner = partnerName(caseData);
  if (partner && !caseData.is_paid) return <Chip size="small" variant="outlined" color="info" label={`Billed to ${partner}`} />;
  // Builder P: the customer chose Pay later (Whish, OMT or cash) and hasn't paid yet.
  const later = !caseData.is_paid && (caseData.pay_later || caseData.payment?.pay_later);
  return <Chip size="small" variant="outlined" color={caseData.is_paid ? "success" : "warning"} label={caseData.is_paid ? "Paid" : later ? "Waiting for payment (Pay later)" : "Not paid"} />;
}

PaymentChip.propTypes = { caseData: PropTypes.object.isRequired };

// The API answers emailed:false when it did not send (asked not to, or the
// mail server refused). When an email was asked for and did not go, say so
// and leave a team note, so nobody believes the customer knows. A partner's
// case sends no email at all (the answer names the partner), so nothing failed.
export async function reportEmail({ asked, emailed, partner, caseId, what, dispatch }) {
  if (!asked || emailed !== false || partner) return;
  toast.warn("Saved, but the email to the customer failed. Tell them another way.");
  try {
    await casesApi.note(caseId, `Email to customer failed: ${what}`, dispatch);
  } catch {
    /* the toast above already says it */
  }
}

// "This application isn't paid. Continue anyway?" Asked once per case visit:
// guard(run) runs straight away when paid or already answered.
export function useUnpaidGuard(isPaid) {
  const [pending, setPending] = useState(null);
  const answered = useRef(false);
  const guard = useCallback(
    (run) => {
      if (isPaid || answered.current) return run();
      return new Promise((resolve) => setPending({ run, resolve }));
    },
    [isPaid],
  );
  const stop = () => {
    pending.resolve(false);
    setPending(null);
  };
  const dialog = pending ? (
    <Dialog open onClose={stop} maxWidth="xs" fullWidth aria-labelledby="unpaid-title">
      <DialogTitle id="unpaid-title">This application isn’t paid. Continue anyway?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          The customer has not paid yet. Usually an application is filed and approved only after payment. Continue only if payment was arranged
          another way.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={stop}>
          Stop
        </ActionButton>
        <ActionButton
          size="medium"
          onClick={async () => {
            answered.current = true;
            const { run, resolve } = pending;
            setPending(null);
            resolve(await run());
          }}
        >
          Continue anyway
        </ActionButton>
      </DialogActions>
    </Dialog>
  ) : null;
  return { guard, dialog };
}

// Each traveller's request (visa file, expiry, status), read from the classic
// request: the case itself does not carry them yet.
export function useTravellerVisas(travellers) {
  const dispatch = useDispatch();
  const ids = travellers.map((t) => t.request_id).join(",");
  const [visas, setVisas] = useState({ loading: true, byId: {} });
  const load = useCallback(async () => {
    const list = ids ? ids.split(",").map(Number) : [];
    const entries = await Promise.all(
      list.map((id) =>
        getRequest(id, dispatch)
          .then((r) => [id, r?.data || null])
          .catch(() => [id, null]),
      ),
    );
    setVisas({ loading: false, byId: Object.fromEntries(entries) });
  }, [ids, dispatch]);
  useEffect(() => {
    load();
  }, [load]);
  return { ...visas, reload: load };
}

// A traveller's visa is on file: from the case (cases/get visa.file) or, on
// an older API, from the classic request.
export const hasVisa = (traveller, request) => !!(traveller.visa?.file || request?.visa);
